import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getJobDetails } from "../../../../apiManager/houseOwner/jobDetails";
import { JobHeader } from "./detail/JobHeader";
import { JobStatusBanner } from "./detail/JobStatusBanner";
import { JobInformation } from "./detail/JobInformation";
import { ServiceInstructions } from "./detail/ServiceInstructions";
import { LoadingState } from "./detail/LoadingState";
import { ErrorState } from "./detail/ErrorState";
import { Card, CardHeader, CardContent, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { ListChecks } from "lucide-react";
import { FaToolbox } from "react-icons/fa";

export function JobDetailTab() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function fetchJobDetails() {
    try {
      setLoading(true);
      const data = await getJobDetails(jobId);
      console.log("Fetched job details:", data);
      if (data.error) {
        setError(data.error);
      } else {
        setJobData(data);
      }
    } catch (err) {
      setError("Failed to fetch job details");
      console.error("Error fetching job details:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} navigate={navigate} />;
  if (!jobData) return <ErrorState error="No job found" navigate={navigate} />;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <JobHeader jobData={jobData} jobId={jobId} />

      {/* Full width banner */}
      <JobStatusBanner job={jobData} />

      {/* Single card with left and right sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left side - Service Information */}
        <div className="lg:col-span-2">
          <JobInformation jobData={jobData} />
        </div>

        {/* Right side - Equipment & Instructions in single card */}
        <Card>
          <CardContent className="mt-6 space-y-6">
            {/* Service Equipment Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FaToolbox className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Equipment Provided</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobData.serviceDetail.serviceEquipment && jobData.serviceDetail.serviceEquipment.length > 0 ? (
                  jobData.serviceDetail.serviceEquipment.map((equipment, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                    >
                      {equipment}
                    </Badge>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">
                    No equipment specified
                  </p>
                )}
              </div>
            </div>

            {/* Service Instructions Section */}
            <ServiceInstructions jobData={jobData} onUpdate={fetchJobDetails} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}